import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../api/axios';
import { toast } from '../components/Toast';
import './Onboarding.css';

const STEPS = ['Create Branch', 'Invite Users', 'Done'];

function Onboarding() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);

  const [branch, setBranch] = useState({ name: '', address: '' });
  const [invites, setInvites] = useState([{ email: '', role: 'STAFF' }]);

  const handleBranchSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post('/company/branches', branch);
      setStep(1);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create branch');
    } finally {
      setLoading(false);
    }
  };

  const handleInviteChange = (index, field, value) => {
    setInvites(prev => prev.map((inv, i) => i === index ? { ...inv, [field]: value } : inv));
  };

  const addInvite = () => setInvites(prev => [...prev, { email: '', role: 'STAFF' }]);
  const removeInvite = (index) => setInvites(prev => prev.filter((_, i) => i !== index));

  const handleInvitesSubmit = async (e) => {
    e.preventDefault();
    const filled = invites.filter(inv => inv.email.trim());
    if (filled.length === 0) { setStep(2); return; }
    setLoading(true);
    try {
      await Promise.all(filled.map(inv => axios.post('/company/users/invite', inv)));
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send invites');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="onboarding-container">
      <div className="onboarding-card">
        <div className="onboarding-header">
          <h1>Welcome! Let's get you set up</h1>
          <div className="step-indicators">
            {STEPS.map((label, i) => (
              <div key={i} className={`step-indicator ${i === step ? 'active' : i < step ? 'done' : ''}`}>
                <span className="step-number">{i < step ? '✓' : i + 1}</span>
                <span className="step-label">{label}</span>
              </div>
            ))}
          </div>
        </div>

        {step === 0 && (
          <form onSubmit={handleBranchSubmit} className="onboarding-form">
            <h2>Create your first branch</h2>
            <p className="step-description">Branches help you organize your teams and projects by location.</p>
            <div className="form-group">
              <label>Branch Name *</label>
              <input
                type="text"
                value={branch.name}
                onChange={e => setBranch(p => ({ ...p, name: e.target.value }))}
                placeholder="e.g. Head Office"
                required
              />
            </div>
            <div className="form-group">
              <label>Address</label>
              <textarea
                value={branch.address}
                onChange={e => setBranch(p => ({ ...p, address: e.target.value }))}
                placeholder="Branch address"
                rows={3}
              />
            </div>
            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Creating...' : 'Create Branch & Continue'}
              </button>
            </div>
          </form>
        )}

        {step === 1 && (
          <form onSubmit={handleInvitesSubmit} className="onboarding-form">
            <h2>Invite your team</h2>
            <p className="step-description">Add team members by email. You can always invite more later.</p>
            {invites.map((inv, i) => (
              <div key={i} className="invite-row">
                <input
                  type="email"
                  value={inv.email}
                  onChange={e => handleInviteChange(i, 'email', e.target.value)}
                  placeholder="colleague@company.com"
                />
                <select value={inv.role} onChange={e => handleInviteChange(i, 'role', e.target.value)}>
                  <option value="STAFF">Staff</option>
                  <option value="BRANCH_MANAGER">Branch Manager</option>
                  <option value="COMPANY_ADMIN">Company Admin</option>
                </select>
                {invites.length > 1 && (
                  <button type="button" className="btn-remove" onClick={() => removeInvite(i)}>✕</button>
                )}
              </div>
            ))}
            <button type="button" className="btn btn-secondary btn-sm" onClick={addInvite}>
              + Add another
            </button>
            <div className="form-actions">
              <button type="button" className="btn btn-ghost" onClick={() => setStep(2)}>
                Skip for now
              </button>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Sending...' : 'Send Invites & Continue'}
              </button>
            </div>
          </form>
        )}

        {step === 2 && (
          <div className="onboarding-complete">
            <div className="success-icon">🎉</div>
            <h2>You're all set!</h2>
            <p>Your workspace is ready. Start managing your construction projects.</p>
            <button className="btn btn-primary btn-lg" onClick={() => navigate('/')}>
              Go to Dashboard
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default Onboarding;
